import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import moment from "moment";
import API_BASE_URL from "../config";
import { toast } from "react-toastify";
import "../pages/ServiciosRegistradosPage.css";
import "./RegistradosDL.css";

/**
 * Pestaña "Registrados" para vuelos de DELTA en la estación del usuario.
 * Reusa las MISMAS clases CSS que ServiciosRegistradosPage (AM) para que el
 * diseño sea idéntico: tarjeta-grupo, tarjeta-encabezado, tabla-reg grupo,
 * fila-servicio, btn-asignar, btn-noshow, tags de urgencia, etc.
 */
const SILLAS = ["WCHR", "WCHS", "SP", "PAS"];

export default function RegistradosDLTab({ estacion, token, decoded }) {
  const [servicios, setServicios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busqueda, setBusqueda] = useState("");
  const [asignando, setAsignando] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [servicioSeleccionado, setServicioSeleccionado] = useState(null);
  const [colaboradores, setColaboradores] = useState([]);
  const [busquedaSiglas, setBusquedaSiglas] = useState("");

  const estacionUsuario = (estacion ?? "").trim().toUpperCase();

  const fetchServicios = useCallback(async () => {
    try {
      const res = await axios.get(
        `${API_BASE_URL}api/servicios/serviciosStatus?status=REGISTRADO&soloAerolineas=DL`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = res.data || [];
      const filtrados = estacionUsuario
        ? data.filter((s) => (s.estacion ?? "").trim().toUpperCase() === estacionUsuario)
        : data;
      setServicios(filtrados);
    } catch (e) {
      const status = e.response?.status;
      if (status === 401 || status === 403) return;
      toast.error("Error al obtener servicios registrados de DL");
    } finally {
      setLoading(false);
    }
  }, [estacionUsuario, token]);

  useEffect(() => {
    fetchServicios();
    const iv = setInterval(fetchServicios, 30000);
    return () => clearInterval(iv);
  }, [fetchServicios]);

  // Agrupa por vuelo + fecha, con la misma metadata que usa AM
  const grupos = useMemo(() => {
    const q = busqueda.trim().toUpperCase();
    const filtrados = q
      ? servicios.filter((s) =>
          (s.nombrePax || "").toUpperCase().includes(q) ||
          (s.pnr || "").toUpperCase().includes(q) ||
          (s.noVuelo || "").toUpperCase().includes(q) ||
          (s.noAsiento || "").toUpperCase().includes(q))
      : servicios;

    const m = new Map();
    for (const s of filtrados) {
      const key = `DL${s.noVuelo || "?"}-${s.fechaInput || moment().format("YYYY-MM-DD")}`;
      if (!m.has(key)) m.set(key, { key, servicios: [], vuelo: `DL${s.noVuelo || "?"}`, aerolinea: "DL", fecha: s.fechaInput, horaVuelo: s.horaVuelo });
      m.get(key).servicios.push(s);
    }

    return Array.from(m.values()).map((g) => {
      const hoy = moment().format("YYYY-MM-DD");
      const esHoy = g.fecha === hoy;
      const minutosRestantes = g.horaVuelo ? moment(g.horaVuelo).diff(moment(), "minutes") : null;

      let urgenciaNivel = "sin-hora";
      let urgenciaEtiqueta = "Sin hora";
      let estadoTiempo = "sin-hora";
      let tiempoRestanteTexto = "Hora del vuelo no registrada";
      if (minutosRestantes !== null) {
        if (minutosRestantes < 0) {
          urgenciaNivel = "pasado"; urgenciaEtiqueta = "Vuelo concluido"; estadoTiempo = "pasado";
          tiempoRestanteTexto = `Vuelo salió hace ${Math.abs(minutosRestantes)} min`;
        } else if (minutosRestantes <= 15) {
          urgenciaNivel = "alta"; urgenciaEtiqueta = "Urgente"; estadoTiempo = "presente";
          tiempoRestanteTexto = `Faltan ${minutosRestantes} min`;
        } else if (minutosRestantes <= 30) {
          urgenciaNivel = "media"; urgenciaEtiqueta = "Prioritario"; estadoTiempo = "presente";
          tiempoRestanteTexto = `Faltan ${minutosRestantes} min`;
        } else {
          urgenciaNivel = "baja"; urgenciaEtiqueta = "En tiempo"; estadoTiempo = "futuro";
          const h = Math.floor(minutosRestantes / 60);
          const m = minutosRestantes % 60;
          tiempoRestanteTexto = h > 0 ? `Faltan ${h}h ${m}m` : `Faltan ${m} min`;
        }
      }

      const esCritico = minutosRestantes !== null && minutosRestantes >= 0 && minutosRestantes <= 30;
      const resumenTipos = g.servicios[0]?.tipoVuelo || "LLEGADAS";
      const fechaVueloTexto = g.fecha ? moment(g.fecha).format("dddd D [de] MMMM") : "Fecha por definir";
      const horaVueloTexto = g.horaVuelo ? moment(g.horaVuelo).format("HH:mm") : "Hora N/D";

      return {
        ...g,
        esHoy, esCritico,
        urgenciaNivel, urgenciaEtiqueta, estadoTiempo, tiempoRestanteTexto,
        resumenTipos, fechaVueloTexto, horaVueloTexto,
        coordinadoresNombres: [],
      };
    }).sort((a, b) => {
      // ordenar: hoy primero, luego por hora
      if (a.esHoy && !b.esHoy) return -1;
      if (!a.esHoy && b.esHoy) return 1;
      const ha = a.horaVuelo ? moment(a.horaVuelo).valueOf() : Infinity;
      const hb = b.horaVuelo ? moment(b.horaVuelo).valueOf() : Infinity;
      return ha - hb;
    });
  }, [servicios, busqueda]);

  const pendCount = servicios.filter((s) => s.statusServicio === "REGISTRADO").length;

  const cambiarSilla = async (id, nueva) => {
    try {
      await axios.patch(
        `${API_BASE_URL}api/pasajerosDL/${id}/silla`,
        { tipoSilla: nueva },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setServicios((prev) => prev.map((s) => s._id === id ? { ...s, tipoSilla: nueva.toUpperCase() } : s));
      toast.success(`Silla actualizada a ${nueva}`);
    } catch {
      toast.error("No se pudo cambiar la silla.");
    }
  };

  const abrirModalAsignar = async (servicio) => {
    try {
      const res = await axios.get(`${API_BASE_URL}api/bitacora/en-turno-detalles`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const disponibles = (res.data || []).filter(
        (c) => (c.estacion ?? "").trim().toUpperCase() === estacionUsuario
      );
      setColaboradores(disponibles);
      setServicioSeleccionado(servicio);
      setBusquedaSiglas("");
      setModalVisible(true);
    } catch {
      toast.error("Error al obtener colaboradores");
    }
  };

  const asignarColaborador = async (colab) => {
    if (!servicioSeleccionado || asignando) return;
    setAsignando(true);
    try {
      await axios.put(
        `${API_BASE_URL}api/servicios/msg/${servicioSeleccionado._id}/${servicioSeleccionado.tipoService ?? servicioSeleccionado.tipoVuelo}/${servicioSeleccionado.aerolinea}/${servicioSeleccionado.noVuelo}`,
        {
          noColaborador: colab.noColaborador,
          statusServicio: "ASIGNADO",
          USUARIO: decoded?.usuario,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`✅ Asignado a ${colab.nombreCompleto}`);
      setModalVisible(false);
      setServicioSeleccionado(null);
      fetchServicios();
    } catch {
      toast.error("❌ Error al asignar servicio");
    } finally {
      setAsignando(false);
    }
  };

  const marcarNoShow = async (servicio) => {
    const servicioId = servicio._id || servicio.noServicio;
    if (!servicioId) { toast.error("No se pudo identificar el servicio."); return; }
    if (!window.confirm(`¿Marcar a ${servicio.nombrePax} como NOSHOW?`)) return;
    try {
      await axios.put(
        `${API_BASE_URL}api/servicios/${servicioId}`,
        {
          statusServicio: "NOSHOW",
          horaFin: moment().tz("America/Mexico_City").format("YYYY-MM-DD HH:mm:ss"),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.info("🚫 Servicio marcado como NOSHOW");
      fetchServicios();
    } catch {
      toast.error("Error al marcar NOSHOW.");
    }
  };

  return (
    <div className="servicios-registrados-page dl-registrados-scope">
      <div className="dl-reg-toolbar">
        <h1 className="dl-reg-title-line">
          Pasajeros registrados <span className="dl-reg-title-badge">DELTA</span>
        </h1>
        <p className="dl-reg-sub-line">
          {estacionUsuario || "—"} · <b>{pendCount}</b> pendientes · {servicios.length} en pantalla
        </p>
      </div>

      <div className="controles-superiores">
        <div className="buscador-vuelo">
          <label>Buscar por PNR, nombre, vuelo o asiento</label>
          <input
            type="text"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            placeholder="Ej. GE8AMZ, HEREDIA, DL590…"
          />
        </div>
        <div className="resumen-servicios">
          <span>{pendCount} pendientes de asignar</span>
        </div>
      </div>

      <div className="layout-servicios">
        <div className="panel-principal">
          <div className="scroll-wrapper">
            {loading ? (
              <div className="sin-resultados"><p>Cargando pasajeros registrados…</p></div>
            ) : grupos.length === 0 ? (
              <div className="sin-resultados">
                <p>{servicios.length === 0
                  ? "Aún no hay pasajeros registrados. Se refresca cada 30 segundos."
                  : "No hay servicios que coincidan con la búsqueda."}</p>
              </div>
            ) : (
              <div className="contenedor-grupos">
                {grupos.map((grupo) => (
                  <section key={grupo.key} className={`tarjeta-grupo ${grupo.esCritico ? "grupo-critico" : ""}`}>
                    <header className="tarjeta-encabezado">
                      <div className="tarjeta-descripcion">
                        <h2>{grupo.vuelo}</h2>
                        <p>
                          {grupo.aerolinea} • {grupo.resumenTipos} • {grupo.vuelo}
                        </p>
                        <p className={`detalle-fecha ${grupo.esHoy ? "fecha-hoy" : "fecha-otro-dia"}`}>
                          {grupo.fechaVueloTexto} {grupo.esHoy ? "(Hoy)" : ""}
                        </p>
                        <p className={`tiempo-restante ${grupo.estadoTiempo}`}>{grupo.tiempoRestanteTexto}</p>
                      </div>
                      <div className="tarjeta-metadata">
                        <span className="tag">{grupo.horaVueloTexto}</span>
                        <span className={`tag tag-urgencia ${grupo.urgenciaNivel}`}>{grupo.urgenciaEtiqueta}</span>
                        <span className={`tag ${grupo.esHoy ? "tag-hoy" : "tag-alerta"}`}>
                          {grupo.esHoy ? "Servicio del día" : "Fecha distinta"}
                        </span>
                        <span className="tag secundario">
                          {grupo.servicios.length} {grupo.servicios.length === 1 ? "servicio" : "servicios"}
                        </span>
                      </div>
                    </header>

                    <table className="tabla-reg grupo">
                      <thead>
                        <tr>
                          <th>PNR</th>
                          <th>Pasajero</th>
                          <th>Asiento</th>
                          <th>Sala</th>
                          <th>Hora registro</th>
                          <th>Tipo silla</th>
                          <th>Acción</th>
                        </tr>
                      </thead>
                      <tbody>
                        {grupo.servicios.map((serv) => {
                          const minutosRestantes = serv.horaVuelo ? moment(serv.horaVuelo).diff(moment(), "minutes") : null;
                          const esCritico = minutosRestantes !== null && minutosRestantes >= 0 && minutosRestantes <= 30;
                          let urgenciaFila = "sin-hora";
                          if (minutosRestantes !== null) {
                            if (minutosRestantes < 0) urgenciaFila = "pasado";
                            else if (minutosRestantes <= 15) urgenciaFila = "alta";
                            else if (minutosRestantes <= 30) urgenciaFila = "media";
                            else urgenciaFila = "baja";
                          }
                          const clasesFila = ["fila-servicio", `fila-urgencia-${urgenciaFila}`, esCritico ? "fila-alerta-urgente" : ""].filter(Boolean).join(" ");

                          return (
                            <tr key={serv._id} className={clasesFila}>
                              <td className="dl-reg-mono">{serv.pnr || "—"}</td>
                              <td>
                                <strong>{serv.nombrePax}</strong>
                                <span className="detalle-servicio">
                                  {serv.noVuelo} • {serv.aerolinea}
                                  {serv.origenVuelo && ` • ${serv.origenVuelo} → ${serv.destinoVuelo}`}
                                </span>
                              </td>
                              <td>{serv.noAsiento || "—"}</td>
                              <td>{serv.sala || "—"}</td>
                              <td>{moment(serv.horaInicio).format("HH:mm")}</td>
                              <td>
                                <select
                                  value={(serv.tipoSilla || "WCHR").toUpperCase()}
                                  onChange={(e) => cambiarSilla(serv._id, e.target.value)}
                                  className="dl-reg-select"
                                >
                                  {SILLAS.map((si) => <option key={si} value={si}>{si}</option>)}
                                </select>
                              </td>
                              <td>
                                <div className="acciones-tabla">
                                  <button className="btn-asignar" onClick={() => abrirModalAsignar(serv)}>
                                    Asignar
                                  </button>
                                  <button className="btn-noshow" onClick={() => marcarNoShow(serv)}>
                                    NOSHOW
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </section>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal asignar */}
      {modalVisible && servicioSeleccionado && (
        <div className="modal-asignar-overlay" onClick={() => !asignando && setModalVisible(false)}>
          <div className="modal-asignar" onClick={(e) => e.stopPropagation()}>
            <div className="modal-asignar-header">
              <h3>Asignar operador</h3>
              <p>DL{servicioSeleccionado.noVuelo} · {servicioSeleccionado.nombrePax}</p>
            </div>
            <input
              type="search"
              placeholder="Buscar por siglas o nombre"
              value={busquedaSiglas}
              onChange={(e) => setBusquedaSiglas(e.target.value.toUpperCase())}
              className="modal-asignar-search"
              autoFocus
            />
            <div className="modal-asignar-list">
              {colaboradores
                .filter((c) => (c.siglas || "").toUpperCase().includes(busquedaSiglas) || (c.nombreCompleto || "").toUpperCase().includes(busquedaSiglas))
                .map((c, i) => (
                  <div key={c.noColaborador} className="modal-asignar-row">
                    <div className="modal-asignar-num">{i + 1}</div>
                    <div className="modal-asignar-info">
                      <div className="modal-asignar-name">{c.nombreCompleto}</div>
                      <div className="modal-asignar-siglas">{c.siglas} · #{c.noColaborador}</div>
                    </div>
                    <button className="btn-asignar" onClick={() => asignarColaborador(c)} disabled={asignando}>
                      {asignando ? "Asignando…" : "Seleccionar"}
                    </button>
                  </div>
                ))}
              {colaboradores.length === 0 && (
                <div className="modal-asignar-empty">No hay operadores en turno para {estacionUsuario}.</div>
              )}
            </div>
            <div className="modal-asignar-footer">
              <button className="btn-cancelar-modal" onClick={() => setModalVisible(false)} disabled={asignando}>
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
